import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Collapse, Drawer, IconButton, Stack } from '@mui/material'
import moment from 'moment'
import 'moment/locale/ru'
import { FC, useMemo, useState } from 'react'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import {
	ShiftDrawerShiftList,
	SHIFT_DRAWER_STAT_LABEL_WIDTH,
} from './ShiftDrawerShiftList'
import type { ShiftCellAssignment } from './ShiftScheduleGrid'
import {
	getEmployeeMonthStats,
	getMonthsInDateRange,
	splitEmployeeAssignments,
} from './shiftEmployeeDrawerUtils'

moment.locale('ru')

const STAT_LABEL_WIDTH = SHIFT_DRAWER_STAT_LABEL_WIDTH

interface StatRowProps {
	label: string
	value: string
}

const StatRow: FC<StatRowProps> = ({ label, value }) => (
	<Stack direction='row' alignItems='flex-start'>
		<Box sx={{ width: STAT_LABEL_WIDTH, flexShrink: 0 }}>
			<TypographyPrimary sx={{ color: '#8D8D96', fontSize: '16px' }}>
				{label}
			</TypographyPrimary>
		</Box>
		<TypographyPrimary sx={{ fontSize: '16px' }}>{value}</TypographyPrimary>
	</Stack>
)

interface ShiftEmployeeDrawerProps {
	open: boolean
	employeeName: string
	dateRange: { startDate: number; endDate: number }
	assignments: ShiftCellAssignment[]
	onClose: () => void
	onDeleteShift: (assignment: ShiftCellAssignment) => void
}

export const ShiftEmployeeDrawer: FC<ShiftEmployeeDrawerProps> = ({
	open,
	employeeName,
	dateRange,
	assignments,
	onClose,
	onDeleteShift,
}) => {
	const [completedExpanded, setCompletedExpanded] = useState(true)

	const months = useMemo(
		() => getMonthsInDateRange(dateRange.startDate, dateRange.endDate),
		[dateRange.endDate, dateRange.startDate],
	)

	const monthStats = useMemo(
		() => months.map(month => getEmployeeMonthStats(assignments, month.key)),
		[assignments, months],
	)

	const { completed, planned } = useMemo(
		() => splitEmployeeAssignments(assignments),
		[assignments],
	)

	return (
		<Drawer
			anchor='right'
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: '520px',
					maxWidth: '100vw',
					p: '24px',
				},
			}}
		>
			<Stack direction='column' gap='24px'>
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='flex-start'
				>
					<TypographyPrimary
						sx={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.2 }}
					>
						{employeeName}
					</TypographyPrimary>
					<IconButton onClick={onClose} size='small' sx={{ mt: '-4px' }}>
						<CloseIcon sx={{ fontSize: '24px', color: '#7F7F84' }} />
					</IconButton>
				</Stack>

				<Stack direction='column' gap='20px'>
					{monthStats.map(stats => (
						<Box key={stats.key}>
							<TypographyPrimary
								sx={{ fontSize: '16px', fontWeight: 700, mb: '12px' }}
							>
								{`За ${stats.label}`}
							</TypographyPrimary>
							<Stack direction='column' gap='12px'>
								<StatRow
									label='Отработано:'
									value={
										stats.totalCount > 0
											? `${stats.workedCount}/${stats.totalCount} смен`
											: '0 смен'
									}
								/>
								<StatRow
									label='Последняя смена:'
									value={
										stats.lastShift
											? `${stats.lastShift.title}, ${moment(stats.lastShift.date).format('DD.MM.YY')}`
											: '—'
									}
								/>
							</Stack>
						</Box>
					))}
				</Stack>

				{completed.length > 0 && (
					<Box>
						<Stack
							direction='row'
							alignItems='center'
							justifyContent='space-between'
							onClick={() => setCompletedExpanded(prev => !prev)}
							sx={{ cursor: 'pointer', userSelect: 'none' }}
						>
							<TypographyPrimary sx={{ fontSize: '16px', fontWeight: 700 }}>
								Выполненные смены
							</TypographyPrimary>
							<IconButton
								size='small'
								sx={{
									transform: completedExpanded
										? 'rotate(180deg)'
										: 'rotate(0deg)',
									transition: 'transform 0.2s',
								}}
							>
								<ExpandMoreIcon sx={{ color: '#7F7F84' }} />
							</IconButton>
						</Stack>
						<Collapse in={completedExpanded}>
							<Box sx={{ pt: '14px' }}>
								<ShiftDrawerShiftList assignments={completed} />
							</Box>
						</Collapse>
					</Box>
				)}

				<Box>
					<TypographyPrimary
						sx={{ fontSize: '16px', fontWeight: 700, mb: '14px' }}
					>
						Запланированные смены
					</TypographyPrimary>
					{planned.length > 0 ? (
						<ShiftDrawerShiftList
							assignments={planned}
							showDelete
							onDelete={onDeleteShift}
						/>
					) : (
						<TypographyPrimary sx={{ color: '#8D8D96', fontSize: '16px' }}>
							Смены пока не назначены
						</TypographyPrimary>
					)}
				</Box>
			</Stack>
		</Drawer>
	)
}
